import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Receipt,
  DollarSign,
  Building2,
  Bot,
  Layers,
  Settings2,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
} from "recharts";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  adminBilling,
  adminListPlans,
  adminAssignPlan,
  getDashboard,
} from "@/lib/api";

const WINDOWS = [
  { v: 1, label: "1 mes" },
  { v: 3, label: "3 meses" },
  { v: 6, label: "6 meses" },
  { v: 9, label: "9 meses" },
  { v: 12, label: "12 meses" },
];

export default function AdminBilling() {
  const [window, setWindow] = useState(1);
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const [b, p, dash] = await Promise.all([
        adminBilling(window),
        adminListPlans(),
        getDashboard(window),
      ]);
      setBilling(b);
      setPlans(p.plans || []);
      setAgents(dash.agents || []);
    } catch {
      toast.error("Error cargando facturación.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const assignedSlugs = useMemo(() => new Set((billing?.lines || []).map((l) => l.agent_slug)), [billing]);

  const onAssign = async (slug, planId) => {
    try {
      setAssigning(slug);
      await adminAssignPlan(slug, planId || null);
      toast.success(planId ? "Plan asignado." : "Asignación retirada.");
      await load();
    } catch {
      toast.error("Error en la asignación.");
    } finally {
      setAssigning("");
    }
  };

  const grand = billing?.grand_total || { agents: 0, units: 0, base: 0, variable: 0, total: 0 };
  const byPlan = (billing?.by_plan || []).filter((p) => p.agents > 0);
  const planById = useMemo(() => Object.fromEntries(plans.map((p) => [p.id, p])), [plans]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900" data-testid="admin-billing-page">
      <TopNav
        right={
          <div className="flex items-center gap-2">
            <Link to="/admin/planes">
              <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                <Settings2 className="mr-2 h-3.5 w-3.5" /> Planes
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                Dashboard
              </Button>
            </Link>
          </div>
        }
      />

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow text-accent-blue">Administración</p>
            <h1 className="mt-3 font-display text-4xl font-medium text-slate-900 sm:text-5xl">
              Facturación de Agentes
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-600">
              Cálculo automático de la facturación de cada agente según el plan asignado y los eventos
              registrados (tareas completadas, feedback, chats) en la ventana seleccionada.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Ventana</span>
            <Select value={String(window)} onValueChange={(v) => setWindow(Number(v))}>
              <SelectTrigger className="h-10 w-[150px] rounded-sm border-slate-300 bg-white text-xs uppercase tracking-[0.18em]" data-testid="window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOWS.map((w) => (
                  <SelectItem key={w.v} value={String(w.v)} data-testid={`window-opt-${w.v}`}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        <section className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="billing-kpis">
          <Kpi icon={Bot} label="Agentes facturados" value={grand.agents || 0} />
          <Kpi icon={Layers} label="Unidades cobradas" value={(grand.units || 0).toLocaleString("es-419")} />
          <Kpi icon={DollarSign} label="Tarifa base" value={`$${(grand.base || 0).toLocaleString("es-419", { maximumFractionDigits: 2 })}`} />
          <Kpi icon={DollarSign} label="Variable" value={`$${(grand.variable || 0).toLocaleString("es-419", { maximumFractionDigits: 2 })}`} accent="teal" />
          <Kpi icon={Receipt} label="TOTAL" value={`$${(grand.total || 0).toLocaleString("es-419", { maximumFractionDigits: 2 })}`} accent="teal" emphasis />
        </section>

        {/* Plan breakdown chart + table */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Card title="Total por plan" subtitle={`Ventana: ${window} ${window === 1 ? "mes" : "meses"}`}>
              {loading ? (
                <Spinner />
              ) : byPlan.length === 0 ? (
                <p className="py-6 text-center text-sm italic text-slate-500">Sin asignaciones todavía.</p>
              ) : (
                <div className="h-[280px]" data-testid="billing-plan-chart">
                  <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
                    <BarChart data={byPlan} margin={{ top: 8, right: 32, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" />
                      <XAxis dataKey="plan_name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} width={42} />
                      <ReTooltip contentStyle={{ background: "#0F172A", border: "none", borderRadius: 2, color: "#F8FAFC", fontSize: 12 }} formatter={(v) => [`$${Number(v).toFixed(2)}`, ""]} />
                      <Bar dataKey="base" stackId="a" fill="#0F172A" />
                      <Bar dataKey="variable" stackId="a" fill="#0D9488">
                        <LabelList dataKey="total" position="top" formatter={(v) => `$${Number(v).toFixed(0)}`} style={{ fill: "#0F172A", fontSize: 11, fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>
          <div className="lg:col-span-5">
            <Card title="Resumen por plan" subtitle={`${byPlan.length} plan(es) con agentes`}>
              {byPlan.length === 0 ? (
                <p className="py-4 text-sm italic text-slate-500">—</p>
              ) : (
                <ul className="space-y-3" data-testid="by-plan-list">
                  {byPlan.map((p) => (
                    <li key={p.plan_id} className="flex items-center justify-between border border-slate-100 bg-slate-50/60 px-3 py-2">
                      <div>
                        <p className="font-display text-sm text-slate-900">{p.plan_name}</p>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                          {p.agents} agente(s) · {p.units} unidades
                        </p>
                      </div>
                      <p className="font-mono text-sm font-semibold text-slate-900">${p.total.toFixed(2)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </section>

        {/* Per-agent billing lines + plan assignment */}
        <section className="mt-10">
          <Card title="Líneas de facturación" subtitle={`Asigne o cambie el plan de cada agente. ${billing?.unassigned_agents || 0} agente(s) sin plan.`}>
            {loading ? (
              <Spinner />
            ) : (
              <div className="overflow-x-auto" data-testid="billing-lines-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      <th className="px-3 py-2">Agente</th>
                      <th className="px-3 py-2">Departamento</th>
                      <th className="px-3 py-2">Plan</th>
                      <th className="px-3 py-2">Unidad</th>
                      <th className="px-3 py-2 text-right">Unidades</th>
                      <th className="px-3 py-2 text-right">Base</th>
                      <th className="px-3 py-2 text-right">Variable</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a) => {
                      const line = (billing?.lines || []).find((l) => l.agent_slug === a.slug);
                      const planId = line?.plan_id || "";
                      const plan = planId ? planById[planId] : null;
                      return (
                        <tr key={a.slug} className="border-b border-slate-100 align-top" data-testid={`bill-row-${a.slug}`}>
                          <td className="px-3 py-2">
                            <div className="font-display text-sm font-medium text-slate-900">
                              <Link to={`/agentes/${a.slug}/metricas`} className="hover:text-accent-blue">{a.name}</Link>
                            </div>
                            <div className="font-mono text-[10px] text-slate-500">{a.slug}</div>
                          </td>
                          <td className="px-3 py-2"><span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3 text-slate-400" /> {a.department || "—"}</span></td>
                          <td className="px-3 py-2 min-w-[200px]">
                            <Select
                              value={planId || "__none__"}
                              onValueChange={(v) => onAssign(a.slug, v === "__none__" ? null : v)}
                              disabled={assigning === a.slug}
                            >
                              <SelectTrigger className="h-9 rounded-sm border-slate-300 bg-white text-xs" data-testid={`plan-select-${a.slug}`}>
                                <SelectValue placeholder="— sin plan —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— sin plan —</SelectItem>
                                {plans.map((p) => (
                                  <SelectItem key={p.id} value={p.id} data-testid={`plan-opt-${p.id}`}>
                                    {p.name} · ${p.base_fee_usd}/mes + ${p.unit_price_usd}/{p.unit_bundle_size}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2 font-mono text-[11px] text-slate-700">
                            {plan?.unit_event_type || "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm">
                            {line ? line.units : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm">
                            {line ? `$${line.base.toFixed(2)}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm text-teal-800">
                            {line ? `$${line.variable.toFixed(2)}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm font-semibold text-slate-900">
                            {line ? `$${line.total.toFixed(2)}` : "—"}
                          </td>
                        </tr>
                      );
                    })}
                    {agents.length === 0 && (
                      <tr><td colSpan={8} className="px-3 py-10 text-center text-sm italic text-slate-500">Sin agentes generados todavía.</td></tr>
                    )}
                  </tbody>
                  {agents.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50">
                        <td colSpan={4} className="px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700">Total</td>
                        <td className="px-3 py-3 text-right font-mono text-sm font-bold text-slate-900">{grand.units}</td>
                        <td className="px-3 py-3 text-right font-mono text-sm font-bold text-slate-900">${grand.base.toFixed(2)}</td>
                        <td className="px-3 py-3 text-right font-mono text-sm font-bold text-teal-800">${grand.variable.toFixed(2)}</td>
                        <td className="px-3 py-3 text-right font-mono text-sm font-bold text-slate-900">${grand.total.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </Card>
        </section>

        <p className="mt-12 text-[11px] uppercase tracking-[0.18em] text-amber-700">
          Cálculo basado en eventos reales del webhook firmado (tareas, feedback) y/o chats. Los planes son
          paramétricos y se gestionan en{" "}
          <Link to="/admin/planes" className="font-semibold text-accent-blue hover:underline">/admin/planes →</Link>.
        </p>
      </main>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-medium text-slate-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex h-[180px] items-center justify-center text-slate-400">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "navy", emphasis }) {
  const dot = accent === "teal" ? "bg-teal-600" : "bg-slate-900";
  return (
    <div className={`relative overflow-hidden border p-5 ${
      emphasis ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"
    }`}>
      <div className="flex items-start justify-between">
        <Icon className={`h-4 w-4 ${emphasis ? "text-accent-blue-light" : "text-slate-400"}`} strokeWidth={1.7} />
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      </div>
      <p className={`mt-5 font-display text-3xl font-semibold leading-none ${emphasis ? "text-white" : "text-slate-900"}`}>{value}</p>
      <p className={`mt-2 text-[10px] uppercase tracking-[0.22em] ${emphasis ? "text-slate-300" : "text-slate-500"}`}>{label}</p>
    </div>
  );
}
